/**
 * Escala de intensidade do mapa de calor (Figma node 233:2602): três faixas
 * por terços do MAIOR volume de célula do período visível.
 *
 * Os cortes são recalculados a cada mudança de granularidade — agrupar por mês
 * produz células maiores que agrupar por dia, então o máximo muda e a leitura
 * das cores acompanha.
 */
export type VolumeIntensity = "empty" | "low" | "medium" | "high";

/** Passo entre colunas na entrada em cascata das células, em ms. */
export const CASCADE_STEP_MS = 25;
/** Teto do atraso: sem ele, uma grade de 90 colunas levaria 2,2s para terminar. */
export const CASCADE_MAX_DELAY_MS = 400;

/** Atraso da animação de entrada de uma célula, pela sua coluna. */
export function cascadeDelayMs(columnIndex: number): number {
  if (!Number.isFinite(columnIndex) || columnIndex <= 0) return 0;
  return Math.min(columnIndex * CASCADE_STEP_MS, CASCADE_MAX_DELAY_MS);
}

export const VOLUME_INTENSITY_CLASS: Record<VolumeIntensity, string> = {
  // Célula sem coleta usa o mesmo tom de "low": no design não há distinção
  // visual entre zero e pouco — a grade precisa continuar alinhada.
  empty: "bg-primary-50",
  low: "bg-primary-50",
  medium: "bg-primary-400",
  high: "bg-primary-800",
};

/**
 * Faixa de uma célula dado o máximo do período.
 *
 * `max <= 0` (nenhum volume em lugar nenhum) devolve tudo como `empty` em vez
 * de dividir por zero.
 */
export function getVolumeIntensity(
  value: number,
  max: number,
): VolumeIntensity {
  if (!Number.isFinite(value) || value <= 0) return "empty";
  if (!Number.isFinite(max) || max <= 0) return "empty";

  const ratio = value / max;
  if (ratio > 2 / 3) return "high";
  if (ratio > 1 / 3) return "medium";
  return "low";
}
